import type React from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { DynamicMap } from './DynamicMap';
import { CommuteTimeCards } from './Commute';
import { useNeighborhoodContextSafe } from './context';
import {
  commuteSection,
  fallbackContainer,
  fallbackIcon,
  fallbackSubtext,
  fallbackText,
  mapContainer,
  mapSection,
  moduleContainer,
} from './neighborhoodModule.styles';

/**
 * NeighborhoodContent — map preview and commute cards for the neighborhood module.
 *
 * Rendered inside NeighborhoodModule (which provides NeighborhoodProvider context).
 * Fetches listing data from Redux.
 */
export const NeighborhoodContent = () => {
  const contextValue = useNeighborhoodContextSafe();

  // Fetch data directly from Redux state
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  // Extract listing data with proper types
  const listing = currentListing
    ? {
        geo: currentListing.geo,
        displayName: currentListing.displayName,
        address: currentListing.address
          ? `${currentListing.address.street}, ${currentListing.address.city}, ${currentListing.address.state}`
          : '',
      }
    : undefined;

  const address = currentListing?.address
    ? `${currentListing.address.street}, ${currentListing.address.city}, ${currentListing.address.state}`
    : undefined;

  // Extract context data safely, with fallbacks for when no provider exists
  const commute = contextValue?.state.commute || {
    destination: null,
    times: [],
    selectedMode: undefined,
    status: 'idle' as const,
  };
  const actions = contextValue?.actions;
  // Validate coordinates are valid numbers within proper ranges
  const isValidCoordinate = (lat?: number, lon?: number): boolean => {
    return (
      typeof lat === 'number' &&
      typeof lon === 'number' &&
      isFinite(lat) &&
      isFinite(lon) &&
      lat >= -90 &&
      lat <= 90 &&
      lon >= -180 &&
      lon <= 180 &&
      lat !== 0 && // Exclude (0,0) as it's likely a missing value
      lon !== 0
    );
  };

  const hasValidListing = listing?.geo?.lat !== undefined && listing?.geo?.lon !== undefined;
  const hasValidCoordinates = hasValidListing && isValidCoordinate(listing.geo.lat, listing.geo.lon);

  // Fallback content when location data is not available
  const FallbackContent = () => (
    <div className={fallbackContainer} aria-label="Location information not available">
      <div className={fallbackIcon}>📍</div>
      <div className={fallbackText}>Location not available</div>
      <div className={fallbackSubtext}>{address ? `Address: ${address}` : 'No address information'}</div>
    </div>
  );

  return (
    <section
      className={moduleContainer}
      data-testid="neighborhood-module"
      role="region"
      aria-label="Neighborhood information"
    >
      <div className={mapSection}>
        <div className={mapContainer}>
          {hasValidCoordinates ? (
            <DynamicMap
              lat={listing.geo.lat}
              lon={listing.geo.lon}
              address={listing.address || address || ''}
              height={240}
              zoom={15}
              commuteDestination={commute.destination}
              selectedCommuteMode={commute.selectedMode}
              allowExpandedBounds={!!commute.destination}
              aria-label={`Map showing location of ${listing.displayName || address || 'property'}`}
            />
          ) : (
            <FallbackContent />
          )}
        </div>

        {/* Display commute time cards when data is available and actions exist */}
        {commute.status === 'success' && commute.destination && commute.times.length > 0 && actions && (
          <div className={commuteSection} aria-label="Commute information">
            <CommuteTimeCards
              destination={commute.destination}
              commuteTimes={commute.times}
              selectedMode={commute.selectedMode}
              onModeSelect={actions.setSelectedMode}
              onEdit={actions.clearCommute}
              data-testid="neighborhood-module-commute-cards"
            />
          </div>
        )}
      </div>
    </section>
  );
};
